import { useEffect, useState, useMemo }         from 'react'
import { useAppStore }                           from '@/tools/exmarked/stores/appStore'
import { getProvider }                           from '@/tools/exmarked/lib/providers'
import { groupByDirectory }                      from '@/tools/exmarked/lib/github'
import { ExPedition }                            from './ExPedition'
import { isLocalSource }                         from '@/tools/exmarked/types'
import type { FileNode }                         from '@/tools/exmarked/types'

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function ListSkeleton() {
  const rows = [28, 22, 35, 20, 30, 25, 18, 32]
  return (
    <div className="px-4 py-3 space-y-3 animate-pulse">
      {rows.map((w, i) => (
        <div key={i} className="flex items-center gap-2.5">
          <div className="w-3.5 h-3.5 rounded bg-forge-slate flex-shrink-0" />
          <div className="h-3.5 bg-forge-slate rounded" style={{ width: `${w * 3}px` }} />
        </div>
      ))}
    </div>
  )
}

// ─── Empty / Error ────────────────────────────────────────────────────────────

function EmptyDocs() {
  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-10 text-center gap-3">
      <svg className="w-7 h-7 text-forge-border" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <p className="text-forge-ash text-xs leading-5">
        No Markdown files found in this source.
      </p>
    </div>
  )
}

function FetchError({ message }: { message: string }) {
  return (
    <div className="p-4 space-y-3">
      <div className="bg-forge-heat-critical/10 border border-forge-heat-critical/30 rounded-lg p-4">
        <p className="text-forge-heat-critical text-xs font-semibold mb-1">Failed to load documents</p>
        <p className="text-forge-ash text-xs leading-relaxed">{message}</p>
      </div>
    </div>
  )
}

// ─── Doc Item ─────────────────────────────────────────────────────────────────

function DocItem({ file }: { file: FileNode }) {
  const { selectFile, selectedFilePath, expedition, addToExpedition } = useAppStore()
  const isActive     = selectedFilePath === file.path
  const inExpedition = expedition.some((f) => f.path === file.path)

  return (
    <div className="group flex items-center gap-1 pr-1">
      <button
        onClick={() => { if (!isActive) selectFile(file.path, file.id) }}
        title={file.path}
        className={[
          'flex-1 flex items-center gap-2 px-3 py-1.5 rounded-md text-left text-sm transition-colors min-w-0',
          isActive
            ? 'bg-forge-ember/15 text-forge-ember'
            : 'text-forge-primary hover:bg-forge-slate/60 hover:text-forge-primary',
        ].join(' ')}
      >
        <svg
          className={`w-3.5 h-3.5 flex-shrink-0 transition-colors ${isActive ? 'text-forge-ember' : 'text-forge-ash group-hover:text-forge-amber'}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <span className="truncate">{file.name}</span>
      </button>

      <button
        onClick={() => addToExpedition(file)}
        title={inExpedition ? 'Already in ExPedition' : 'Add to ExPedition'}
        className={[
          'flex-shrink-0 rounded p-0.5 transition-colors',
          inExpedition
            ? 'text-forge-gold'
            : 'text-transparent group-hover:text-forge-ash hover:!text-forge-gold',
        ].join(' ')}
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d={inExpedition ? 'M5 13l4 4L19 7' : 'M12 4v16m8-8H4'}
          />
        </svg>
      </button>
    </div>
  )
}

// ─── Doc Group ────────────────────────────────────────────────────────────────

function DocGroup({ dir, files }: { dir: string; files: FileNode[] }) {
  const [open, setOpen] = useState(true)
  const label = dir === '' ? '/' : dir

  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-1.5 px-3 py-1 text-left group"
      >
        <svg
          className={`w-2.5 h-2.5 text-forge-ash transition-transform flex-shrink-0 ${open ? 'rotate-90' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-[11px] text-forge-ash font-mono tracking-wide truncate group-hover:text-forge-primary transition-colors">
          {label}
        </span>
      </button>

      {open && (
        <div className="pl-2">
          {files.map((f) => <DocItem key={f.path} file={f} />)}
        </div>
      )}
    </div>
  )
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export function Sidebar() {
  const {
    selectedSource,
    selectedFilePath,
    selectedFileId,
    fileTree,
    isLoadingTree,
    error,
    backToVault,
    setFileTree,
    setError,
    selectFile,
    refreshTree,
  } = useAppStore()

  const [search,    setSearch]    = useState('')
  const [truncated, setTruncated] = useState(false)

  const isLocal = selectedSource ? isLocalSource(selectedSource) : false

  // Fetch .md files whenever a source is selected or manually refreshed
  useEffect(() => {
    if (!selectedSource || !isLoadingTree) return

    let cancelled = false
    setSearch('')
    setTruncated(false)

    const provider = getProvider(selectedSource)

    provider.listFiles(selectedSource)
      .then(({ files, truncated: t }) => {
        if (cancelled) return
        setFileTree(files)
        if (t) setTruncated(true)

        // Detect file changes (GitHub: SHA changed; local: path-based, no re-fetch needed)
        if (selectedFilePath && selectedFileId) {
          const refreshed = files.find((f) => f.path === selectedFilePath)
          if (refreshed && refreshed.id !== selectedFileId) {
            selectFile(refreshed.path, refreshed.id)
            return
          }
        }

        // Auto-open last viewed file for this source
        try {
          const map = JSON.parse(
            localStorage.getItem('exmarked:lastOpened') ?? '{}'
          ) as Record<string, { path: string }>
          const last = map[selectedSource.id]
          if (last) {
            const match = files.find((f) => f.path === last.path)
            if (match) selectFile(match.path, match.id)
          }
        } catch { /* ignore */ }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Unknown error loading documents.')
        setFileTree([])
      })

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSource?.id, isLoadingTree, setFileTree, setError, selectFile])

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    if (!q) return fileTree
    return fileTree.filter(
      (f) => f.name.toLowerCase().includes(q) || f.path.toLowerCase().includes(q)
    )
  }, [fileTree, search])

  const groups = useMemo(() => groupByDirectory(filtered), [filtered])

  const fetchError = error && error !== '__truncated__' ? error : null

  // Sidebar header label and source display name
  const sourceLabel = isLocal ? 'Local Folder' : 'Repository'
  const sourceName  = selectedSource
    ? isLocal
      ? selectedSource.display_name
      : `${(selectedSource as { owner: string }).owner}/${selectedSource.display_name ?? (selectedSource as { name: string }).name}`
    : '—'

  return (
    <aside className="w-72 bg-forge-obsidian border-r border-forge-border flex flex-col overflow-hidden flex-shrink-0">

      {/* Source header */}
      <div className="px-4 py-3 border-b border-forge-border flex-shrink-0">
        <div className="flex items-center gap-2 mb-0.5">
          <button
            onClick={backToVault}
            className="text-forge-ash hover:text-forge-gold transition-colors"
            title="Back to Vault"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <p className="text-xs text-forge-ash uppercase tracking-wider flex-1">{sourceLabel}</p>
          <button
            onClick={refreshTree}
            disabled={isLoadingTree}
            title={isLocal ? 'Refresh folder' : 'Sync with GitHub'}
            className="text-forge-ash hover:text-forge-gold disabled:opacity-40 transition-colors"
          >
            <svg
              className={`w-3.5 h-3.5 ${isLoadingTree ? 'animate-spin' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>
        <p className="text-sm font-medium truncate text-forge-gold" title={sourceName ?? undefined}>
          {sourceName}
        </p>
        {!isLoadingTree && fileTree.length > 0 && (
          <p className="text-[11px] text-forge-ash mt-0.5">
            {fileTree.length} document{fileTree.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-forge-border/50 flex-shrink-0">
        <div className="flex items-center gap-2 bg-forge-void rounded-md px-2.5 py-1.5">
          <svg className="w-3.5 h-3.5 text-forge-ash flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            data-sidebar-search
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents…"
            disabled={isLoadingTree || !!fetchError}
            className="bg-transparent text-xs text-forge-primary placeholder-forge-ash outline-none w-full disabled:opacity-40"
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-forge-ash hover:text-forge-primary transition-colors flex-shrink-0">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Document list */}
      <div className="flex-1 overflow-y-auto py-2 px-1">
        {isLoadingTree ? (
          <ListSkeleton />
        ) : fetchError ? (
          <FetchError message={fetchError} />
        ) : filtered.length === 0 && search ? (
          <p className="text-center text-forge-ash text-xs py-8">
            No documents match "{search}"
          </p>
        ) : fileTree.length === 0 ? (
          <EmptyDocs />
        ) : groups.size === 1 && groups.has('') ? (
          fileTree.map((f) => <DocItem key={f.path} file={f} />)
        ) : (
          Array.from(groups.entries()).map(([dir, files]) => (
            <DocGroup key={dir} dir={dir} files={files} />
          ))
        )}
      </div>

      {/* ExPedition panel */}
      <ExPedition />

      {/* Truncation warning (GitHub only) */}
      {truncated && (
        <div className="px-3 py-2 flex items-start gap-2 bg-forge-amber/5 border-t border-forge-amber/20 flex-shrink-0">
          <svg className="w-3.5 h-3.5 text-forge-amber flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <p className="text-[10px] text-forge-amber/80 leading-4">
            Large repo — some files may not appear.
          </p>
        </div>
      )}
    </aside>
  )
}
